// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The right-hand panel: one report. Owner's decisions 2026-09-18
// (docs/plans/DECISIONS.md D47), replacing the single long column of D37 —
// description, screenshot, ClickUp comments, then the USC discussion — which the
// owner still found cluttered: to read a reply you scrolled past, and waited for,
// the screenshot.
//
//   - A slim header: ticket, status, title, and one line of where / who / when.
//   - The link strip: the earlier report this one is another instance of, or the
//     reports linked to this one, and the way to link or unlink (D47).
//   - Two tabs. Conversation opens first; Details holds the description, the
//     evidence and the screenshot, which is only fetched once Details is opened.

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Link2 } from 'lucide-react';
import {
  BUG_STATUS_LABEL,
  SEVERITY_LABEL,
  getScreenshotUrl,
  linkBugReport,
  unlinkBugReport,
  type BugLinkCandidate,
  type BugReportSummary,
} from '../../lib/bugReportApi';
import { formatDate } from '../../utils/dateFormat';
import Button from '../../components/ui/Button';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { FOCUS, SEGMENT_TRACK, segmentClass } from '../../components/ui/tokens';
import { BugStatusBadge } from './BugStatusBadge';
import { BugReportConversation } from './BugReportConversation';
import { LinkPicker } from './LinkPicker';
import { teamNoteCount, useTeamNotes, type CommentsResult } from './useTeamNotes';

/**
 * The screenshot, fetched only when its report's Details are opened. A thumbnail
 * the frame hugs; the full picture is one click away.
 */
const Screenshot: React.FC<{ id: string; ticketNumber: string }> = ({ id, ticketNumber }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    // Minted on open, not with the list: the link is short-lived by design
    // (utils/urlSigning.js), so minting one per row up front would hand out
    // links that expire before anyone clicks them.
    getScreenshotUrl(id)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'The screenshot could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!url) return <p className="text-sm text-slate-500">Loading screenshot…</p>;

  return (
    <figure className="space-y-1.5">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`inline-block max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white align-top ${FOCUS}`}
      >
        <img src={url} alt={`Screenshot for ${ticketNumber}`} className="block max-h-96 w-auto max-w-full" />
      </a>
      <figcaption>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
        >
          Open full size
          <ExternalLink aria-hidden="true" size={14} />
        </a>
      </figcaption>
    </figure>
  );
};

const Meta: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex min-w-0 gap-2">
    <dt className="w-28 shrink-0 text-slate-500">{label}</dt>
    <dd className="min-w-0 break-words text-slate-700">{value}</dd>
  </div>
);

const Details: React.FC<{ report: BugReportSummary }> = ({ report }) => (
  <div className="space-y-6">
    <section className="space-y-1.5">
      <h3 className="text-sm font-semibold text-slate-900">What happened</h3>
      {report.description ? (
        <p className="max-w-[68ch] whitespace-pre-wrap break-words text-sm leading-[1.55] text-slate-700">{report.description}</p>
      ) : (
        <p className="text-sm text-slate-500">No description was given.</p>
      )}
    </section>

    <dl className="space-y-1 text-xs">
      <Meta label="Screen" value={report.pageLabel || '—'} />
      <Meta label="Reported by" value={report.isMine ? 'You' : report.reporterName || '—'} />
      <Meta label="Filed" value={formatDate(report.createdAt, '—')} />
      <Meta label="Severity" value={SEVERITY_LABEL[report.severity] || report.severity} />
      {report.statusChangedAt ? <Meta label="Status changed" value={formatDate(report.statusChangedAt)} /> : null}
    </dl>

    <section className="space-y-1.5">
      <h3 className="text-sm font-semibold text-slate-900">Screenshot</h3>
      {report.hasScreenshot ? (
        <Screenshot id={report.id} ticketNumber={report.ticketNumber} />
      ) : (
        <p className="text-sm text-slate-500">No screenshot was attached to this report.</p>
      )}
    </section>
  </div>
);

type Tab = 'conversation' | 'details';

/** Two ARIA tabs; the arrow keys move between them. */
const TabBar: React.FC<{
  reportId: string;
  tab: Tab;
  onChange: (tab: Tab) => void;
  conversationCount: number;
}> = ({ reportId, tab, onChange, conversationCount }) => {
  const refs = useRef<Record<Tab, HTMLButtonElement | null>>({ conversation: null, details: null });
  const tabs: Array<{ id: Tab; label: React.ReactNode }> = [
    {
      id: 'conversation',
      label: (
        <>
          Conversation
          {conversationCount > 0 ? <span className="tabular-nums text-slate-500">{conversationCount}</span> : null}
        </>
      ),
    },
    { id: 'details', label: 'Details' },
  ];

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next: Tab = tab === 'conversation' ? 'details' : 'conversation';
    onChange(next);
    refs.current[next]?.focus();
  };

  return (
    <div role="tablist" aria-label="Report" className={SEGMENT_TRACK} onKeyDown={onKeyDown}>
      {tabs.map((t) => {
        const selected = t.id === tab;
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[t.id] = el;
            }}
            type="button"
            role="tab"
            id={`bug-tab-${t.id}-${reportId}`}
            aria-selected={selected}
            aria-controls={`bug-panel-${t.id}-${reportId}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={segmentClass(selected)}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * D47: the earlier report this one is another instance of, or the reports that
 * are instances of this one — and the way to link or unlink it.
 */
const LinkStrip: React.FC<{
  report: BugReportSummary;
  onOpen: (id: string) => void;
  onLinkChanged: (report: BugReportSummary) => void;
}> = ({ report, onOpen, onLinkChanged }) => {
  const confirm = useConfirm();
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const link = report.linkedTo;

  const run = async (action: () => ReturnType<typeof linkBugReport>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await action();
      setPicking(false);
      onLinkChanged(res.data.report);
    } catch (err: any) {
      setError(err?.message || 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const pick = async (candidate: BugLinkCandidate) => {
    const original = candidate.linkedToTicket || candidate.ticketNumber;
    const closed = candidate.status === 'fixed' || candidate.status === 'wont_fix';
    const moving = report.instances.length;
    const summary = [
      closed
        ? `${original} is ${BUG_STATUS_LABEL[candidate.status]}, so ${report.ticketNumber} keeps its own status and is marked as having come back.`
        : `${report.ticketNumber} will show ${original}'s status for as long as they are linked.`,
      moving
        ? `The ${moving} report${moving === 1 ? '' : 's'} linked to ${report.ticketNumber} move${moving === 1 ? 's' : ''} to ${original} with it.`
        : null,
      'The development team is told.',
    ]
      .filter(Boolean)
      .join(' ');
    const confirmed = await confirm({
      title: `Mark ${report.ticketNumber} as the same problem as ${original}?`,
      summary,
      confirmLabel: 'Link',
    });
    if (confirmed) await run(() => linkBugReport(report.id, candidate.id));
  };

  const unlink = async () => {
    if (!link) return;
    const confirmed = await confirm({
      title: `Unlink ${report.ticketNumber} from ${link.ticketNumber}?`,
      summary: 'It goes back to its own status. It can be linked again later.',
      confirmLabel: 'Unlink',
    });
    if (confirmed) await run(() => unlinkBugReport(report.id));
  };

  const linkButton = !link && !picking ? (
    <Button variant="ghost" size="sm" icon={<Link2 aria-hidden="true" className="h-4 w-4" />} onClick={() => setPicking(true)}>
      Same problem as an earlier report…
    </Button>
  ) : null;

  return (
    <div className="space-y-2">
      {link ? (
        <div
          role="group"
          aria-label="Linked report"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
        >
          <Link2 aria-hidden="true" size={16} className="shrink-0 text-slate-400" />
          <p className="min-w-0 flex-1 text-sm text-slate-700">
            {link.kind === 'came_back' ? 'Came back after ' : 'Same problem as '}
            <span className="font-medium text-slate-900">{link.ticketNumber}</span>
            {link.kind === 'came_back' ? ' was fixed' : ''}
            {link.title ? <span className="text-slate-600"> — {link.title}</span> : null}
            {link.kind === 'same' ? (
              <span className="block text-xs text-slate-500">Shows {link.ticketNumber}'s status while linked.</span>
            ) : null}
          </p>
          <div className="flex gap-1">
            {link.title !== null ? (
              <Button variant="ghost" size="sm" onClick={() => onOpen(link.id)}>
                Open
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={unlink} loading={busy}>
              Unlink
            </Button>
          </div>
        </div>
      ) : report.instances.length > 0 ? (
        <div
          role="group"
          aria-label="Also reported as"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
        >
          <Link2 aria-hidden="true" size={16} className="shrink-0 text-slate-400" />
          <span>Seen {report.instances.length + 1} times. Also reported as</span>
          {report.instances.map((instance, i) => (
            <React.Fragment key={instance.id}>
              <button
                type="button"
                onClick={() => onOpen(instance.id)}
                title={instance.title}
                className={`font-medium text-primary-600 hover:underline ${FOCUS}`}
              >
                {instance.ticketNumber}
              </button>
              {i < report.instances.length - 1 ? <span aria-hidden="true">·</span> : null}
            </React.Fragment>
          ))}
          <span className="ml-auto">{linkButton}</span>
        </div>
      ) : (
        linkButton
      )}

      {picking ? (
        <LinkPicker forId={report.id} screen={report.pageLabel} onPick={pick} onCancel={() => setPicking(false)} />
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export const BugReportDetail: React.FC<{
  report: BugReportSummary;
  commentsCache: Map<string, CommentsResult>;
  refreshKey: number;
  /** Below the lg breakpoint the list and the report take turns; this goes back. */
  onBack: () => void;
  /** A comment written, edited or deleted answers with the whole report; this puts it on screen. */
  onReportChanged: (report: BugReportSummary) => void;
  /** A link or unlink can move other reports too (D47); the page reloads its list. */
  onLinkChanged: (report: BugReportSummary) => void;
  /** Open another report: an original, or an instance of this one. */
  onOpen: (id: string) => void;
}> = ({ report, commentsCache, refreshKey, onBack, onReportChanged, onLinkChanged, onOpen }) => {
  // The page keys this panel by report, so a newly picked report opens on Conversation.
  const [tab, setTab] = useState<Tab>('conversation');
  // Details (and its screenshot) mount the first time they are opened, then stay.
  const [detailsOpened, setDetailsOpened] = useState(false);
  const showTab = (next: Tab) => {
    setTab(next);
    if (next === 'details') setDetailsOpened(true);
  };
  const notes = useTeamNotes(report.id, commentsCache, refreshKey);
  const conversationCount = (report.discussion ?? []).length + teamNoteCount(notes.result);
  const meta = [
    report.pageLabel || null,
    report.isMine ? 'You' : report.reporterName || null,
    formatDate(report.createdAt) || null,
    report.severity ? SEVERITY_LABEL[report.severity]?.split(' — ')[0] ?? report.severity : null,
  ].filter(Boolean);

  return (
    <>
      {/* Outside the article: inside it, the hidden row still took the article's
          vertical spacing, and every report opened 24px lower on a wide screen. */}
      <div className="px-4 pt-4 sm:px-6 lg:hidden">
        <Button variant="ghost" size="sm" icon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />} onClick={onBack}>
          All reports
        </Button>
      </div>

      <article aria-labelledby={`bug-title-${report.id}`} className="mx-auto w-full max-w-3xl space-y-4 px-4 py-5 sm:px-6">
        <header className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-slate-500">{report.ticketNumber}</span>
            <BugStatusBadge status={report.status} />
          </div>
          <h2 id={`bug-title-${report.id}`} className="break-words text-base font-semibold leading-6 text-slate-900">
            {report.title}
          </h2>
          {meta.length > 0 ? <p className="text-xs text-slate-500">{meta.join(' · ')}</p> : null}
        </header>

        <LinkStrip report={report} onOpen={onOpen} onLinkChanged={onLinkChanged} />

        <TabBar reportId={report.id} tab={tab} onChange={showTab} conversationCount={conversationCount} />

        {/* Both panels stay in the page, so a half-typed comment survives a look at the Details. */}
        <div
          role="tabpanel"
          id={`bug-panel-conversation-${report.id}`}
          aria-labelledby={`bug-tab-conversation-${report.id}`}
          hidden={tab !== 'conversation'}
          className="pt-1"
        >
          <BugReportConversation report={report} notes={notes} onChanged={onReportChanged} onOpen={onOpen} />
        </div>
        <div
          role="tabpanel"
          id={`bug-panel-details-${report.id}`}
          aria-labelledby={`bug-tab-details-${report.id}`}
          hidden={tab !== 'details'}
          className="pt-1"
        >
          {/* Not mounted until Details is opened: the screenshot is fetched then, not with the report. */}
          {detailsOpened ? <Details report={report} /> : null}
        </div>
      </article>
    </>
  );
};
